import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { Alert, AlertDescription } from '@app/components/ui/alert';

type Duration = number | null;
export type ToastSeverity = 'info' | 'success' | 'warning' | 'error';

interface ToastState {
  message: React.ReactNode;
  severity: ToastSeverity;
  duration: Duration;
}

interface ToastContextType {
  showToast: (message: React.ReactNode, severity?: ToastSeverity, duration?: Duration) => void;
}

const DEFAULT_DURATIONS: Record<ToastSeverity, Duration> = {
  info: 2000,
  success: 2000,
  warning: 4000,
  error: 6000,
};

const TOAST_VARIANTS: Record<ToastSeverity, React.ComponentProps<typeof Alert>['variant']> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'destructive',
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toast, setToast] = useState<ToastState | null>(null);

  const closeToast = useCallback(() => {
    setToast(null);
  }, []);

  const showToast = useCallback(
    (message: React.ReactNode, severity: ToastSeverity = 'info', duration?: Duration) => {
      setToast({
        message,
        severity,
        duration: duration === undefined ? DEFAULT_DURATIONS[severity] : duration,
      });
    },
    [],
  );

  useEffect(() => {
    if (!toast || toast.duration === null) {
      return;
    }

    const timeoutId = window.setTimeout(closeToast, toast.duration);
    return () => window.clearTimeout(timeoutId);
  }, [closeToast, toast]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toast && (
        <div
          className="pointer-events-none fixed inset-x-0 top-4 z-[1400] flex justify-center px-4"
          data-testid="toast-container"
        >
          <Alert
            data-testid="toast"
            variant={TOAST_VARIANTS[toast.severity]}
            onDismiss={closeToast}
            dismissLabel="Close"
            className="pointer-events-auto w-full max-w-[600px] shadow-lg"
          >
            <AlertDescription>{toast.message}</AlertDescription>
          </Alert>
        </div>
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const useOptionalToast = () => useContext(ToastContext);
